import React, { useState } from "react";
import api, { formatTL, formatDateTime } from "@/lib/api";
import { useLiveQuery, invalidate, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Storefront, CheckCircle, Warning } from "@phosphor-icons/react";

function monthRange(month) {
  const [y, m] = month.split("-").map(Number);
  const start = `${y}-${String(m).padStart(2, "0")}-01`;
  const last = new Date(y, m, 0).getDate();
  return { start, end: `${y}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}` };
}

export default function CorporateQuotaPayments({ month, onChanged }) {
  const [row, setRow] = useState(null);
  const [form, setForm] = useState({ amount: "", method: "cash", start: "", end: "", note: "" });
  const [busy, setBusy] = useState(false);

  const { data, refetch } = useLiveQuery(
    [TAGS.SUBSCRIPTION_PAYMENTS, TAGS.CORPORATE],
    async () => (await api.get(`/subscription-payments/corporate-status?month=${month}`)).data,
    { deps: [month] },
  );
  const rows = data?.rows || [];
  if (rows.length === 0) return null;

  const openPay = (r) => {
    const { start, end } = monthRange(month);
    setForm({ amount: String(r.monthly_fee || ""), method: "cash", start, end, note: "" });
    setRow(r);
  };

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Tutar girin");
    setBusy(true);
    try {
      await api.post("/subscription-payments/corporate", {
        corporate_account_id: row.corporate_account_id, amount: Number(form.amount),
        method: form.method, period_start: form.start, period_end: form.end, note: form.note || null,
      });
      toast.success(`${row.name} — ${formatTL(Number(form.amount))} abonelik ödemesi alındı`);
      setRow(null);
      invalidate([TAGS.SUBSCRIPTION_PAYMENTS, TAGS.KASA]);
      refetch(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Card className="rounded-sm border-violet-300">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Storefront size={16} weight="fill" className="text-violet-600" />
            <h3 className="text-sm font-semibold uppercase tracking-widest">Kurumsal Kontenjan Abonelikleri</h3>
            <span className="text-xs text-muted-foreground">— işletmeler plakasız, aylık sabit bedel öder</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>İşletme</TableHead><TableHead>Kontenjan</TableHead><TableHead>Aylık Bedel</TableHead>
                <TableHead>Bu Ay</TableHead><TableHead>Son Ödeme</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.corporate_account_id} data-testid={`corpsub-row-${r.corporate_account_id}`}>
                  <TableCell className="font-semibold">{r.name}</TableCell>
                  <TableCell className="font-mono">{r.quota_slots} araç</TableCell>
                  <TableCell className="font-mono">{r.monthly_fee > 0 ? formatTL(r.monthly_fee) : <span className="text-muted-foreground text-xs">tanımsız</span>}</TableCell>
                  <TableCell>
                    {r.status === "paid_this_month" ? (
                      <Badge className="rounded-sm bg-emerald-600 text-white border-transparent text-[10px] uppercase gap-1" data-testid={`corpsub-status-${r.corporate_account_id}`}>
                        <CheckCircle size={12} weight="fill" /> Ödendi · {formatTL(r.payment.amount)}
                      </Badge>
                    ) : (
                      <Badge className="rounded-sm bg-red-600 text-white border-transparent text-[10px] uppercase gap-1" data-testid={`corpsub-status-${r.corporate_account_id}`}>
                        <Warning size={12} weight="fill" /> Ödenmedi
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.last_payment ? `${formatDateTime(r.last_payment.paid_at)} · ${formatTL(r.last_payment.amount)}` : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.status !== "paid_this_month" && (
                      <Button size="sm" onClick={() => openPay(r)} data-testid={`corpsub-pay-${r.corporate_account_id}`}
                              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-[10px] font-bold">
                        Ödeme Al
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!row} onOpenChange={(o) => !o && setRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{row?.name} — Aylık Abonelik Ödemesi</DialogTitle>
            <DialogDescription>Kontenjan: {row?.quota_slots} araç. Ödeme Kasa ve Banka Aktarımı'nda abonelik geliri olarak görünür.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tutar (₺)</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm font-mono" data-testid="corpsub-amount" /></div>
              <div>
                <Label>Yöntem</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="corpsub-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Nakit</SelectItem>
                    <SelectItem value="card">Kart</SelectItem>
                    <SelectItem value="bank_transfer">EFT / Havale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Dönem Başı</Label><Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="rounded-sm" data-testid="corpsub-start" /></div>
              <div><Label>Dönem Sonu</Label><Input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="rounded-sm" data-testid="corpsub-end" /></div>
            </div>
            <div><Label>Not (opsiyonel)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-sm" placeholder="Fatura no vb." /></div>
            <Button onClick={submit} disabled={busy} data-testid="corpsub-submit"
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              Ödemeyi Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
